import React, {useState, memo, useCallback, useEffect} from 'react';
import {useHistory} from 'react-router-dom';
import {Loading} from './components/common/Loading';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Grid,
    Typography,
} from '@mui/material';

import {useAppSelector} from './redux/functions/hooks';

import {AuthState} from './redux/store/authSlice'

export enum action {
    VIEW = 'viewOk',
    ADD = 'addOk',
    EDIT = 'editOk',
    DELETE = 'deleteOk',
    EXPORT = 'exportOk',
    LOGGING = 'logging',
};



/**
 *  const auth = useAppSelector((x) => x.auth); for Auth prop.
 * @param auth 
 * @param functionId 
 * @param action 
 * @param alsoRequire 
 * @returns 
 */
export const canIFunc = (auth: AuthState, functionId: string, action: action, alsoRequire = () => true) =>
    functionId
        ? auth?.user?.permissions?.some(
              (perm) => perm.functionId === functionId && perm[action.toString()]
          ) && alsoRequire()
        : auth?.authenticated && !auth?.hasError;

const canIConditionalFunc = (
    auth: AuthState,
    functionId: string,
    action: action,
    condition: boolean,
    alsoRequire = () => true
) => {
    return !condition || canIFunc(auth, functionId, action, alsoRequire);
};

interface CanIConditionalProps extends CanIProps {
    condition?: boolean;
}

export const ConditionalCanI = memo(
    ({
        functionId,
        action,
        children,
        condition,
        noPermissions = <></>,
        alsoRequire = () => true,
    }: CanIConditionalProps) => {
        const [iCan, setICan] = useState(false);
        const auth = useAppSelector((x) => x.auth);
        const validatePermissions = useCallback(
            async () =>
                setICan(
                    canIConditionalFunc(
                        auth,
                        functionId,
                        action,
                        condition,
                        alsoRequire
                    )
                ),
            [functionId, action, condition, alsoRequire, auth]
        );

        useEffect(() => {
            validatePermissions();
        }, [auth]);

        return (
            <Loading promise={validatePermissions}>
                {iCan ? children : noPermissions}
            </Loading>
        );
    }
);

interface CanIProps {
    functionId?: string;
    action?: action;
    noPermissions?: React.ReactNode;
    children: React.ReactNode;
    alsoRequire?(): boolean;
}

//Memo because the inputs shouldn't change so we don't need to update this one.
export const CanI = ({
    functionId,
    action,
    children,
    noPermissions = <></>,
    alsoRequire = () => true,
}: CanIProps) => {
    const [iCan, setICan] = useState(false);

    const auth = useAppSelector((x) => x.auth);

    const validatePermissions = useCallback(
        async () => setICan(canIFunc(auth, functionId, action, alsoRequire)),
        [functionId, action, alsoRequire, auth]
    );

    useEffect(() => {
        validatePermissions();
    }, [auth]);

    return (
        <Loading promise={validatePermissions}>
            {iCan ? children : noPermissions}
        </Loading>
    );
};

export const CanIUnAuth = (props) => (
    <CanI noPermissions={<Unauthorized></Unauthorized>} {...props}></CanI>
);

export const Unauthorized = (props) => {
    let history = useHistory();

    return (
        <Container>
            <Card>
                <CardHeader>Insufficient Permissions</CardHeader>
                <CardContent>
                    <Grid container direction="column">
                        <Grid item>
                            <Typography>
                                Oops you don't have the necessary permissions to
                                view that.
                            </Typography>
                            <Typography>
                                Click the button below to get back to where you
                                were.
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button onClick={() => history.goBack()}>
                                Back
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Container>
    );
};
